import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Shirt, Zap, BadgeCheck, Heart, MessageSquare, PenTool, ArrowUpRight, FileText } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { BRANDS } from "@/lib/data";
import { api, resolveImg } from "@/lib/api";

const heroWrap = { hidden: {}, show: { transition: { staggerChildren: 0.13, delayChildren: 0.15 } } };
const heroLine = { hidden: { y: "110%" }, show: { y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } } };
const heroUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } };

const FEATURES = [
  { icon: Shirt, title: "NO MINIMUMS", desc: "Order what you need, no minimum required." },
  { icon: Zap, title: "FAST TURNAROUND", desc: "Quick production & delivery you can count on." },
  { icon: BadgeCheck, title: "PREMIUM QUALITY", desc: "Top brands, high quality prints that last." },
  { icon: Heart, title: "FAMILY OWNED", desc: "Proudly owned & operated in New Jersey." },
];

const STEPS = [
  { icon: MessageSquare, n: "1", title: "TELL US YOUR IDEA", desc: "Share your design, logo, or idea with us." },
  { icon: PenTool, n: "2", title: "WE DESIGN IT", desc: "Our team creates a mockup for your approval." },
  { icon: Shirt, n: "3", title: "WE BRING IT TO LIFE", desc: "High quality printing, fast turnaround, every time." },
];

const CHAPTERS = [
  { n: "01", title: "Made in New Jersey", body: "Every order is printed, pressed and packed by hand in our Oceanport shop — no outsourcing, no shortcuts." },
  { n: "02", title: "Built on relationships", body: "We're a family business. Your team, school or brand isn't a ticket number — it's a neighbor we want to impress." },
  { n: "03", title: "Craft over quantity", body: "From a single tee to a thousand, we treat the press with the same obsessive care and premium materials." },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} className="relative h-[90vh] min-h-[620px] max-h-[940px] overflow-hidden bg-ink" data-testid="hero">
      <motion.div style={{ y: imgY, scale }} className="absolute inset-0">
        <img src="/hero_fam.png" alt="Briars Unlimited family" className="lg:hidden h-full w-full object-contain object-bottom" />
        <img src="/hero_desktop.png" alt="Briars Unlimited family" className="hidden lg:block h-full w-full object-cover object-center" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/10 lg:via-ink/25 lg:to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-ink/20" />

      <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 h-full flex flex-col justify-start pt-[120px] lg:justify-center lg:pt-0">
        <motion.div variants={heroWrap} initial="hidden" animate="show" className="max-w-2xl text-[#f2f0ea] [text-shadow:0_2px_24px_rgba(0,0,0,0.5)]">
          <h1 className="font-display text-[13vw] sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.92]">
            <span className="line-mask"><motion.span className="block" variants={heroLine}>YOUR VISION.</motion.span></span>
            <span className="line-mask"><motion.span className="block" variants={heroLine}>OUR CRAFT.</motion.span></span>
          </h1>
          <motion.p variants={heroUp} className="font-script text-gold text-5xl sm:text-6xl mt-2 mb-6">
            Unlimited Possibilities.
          </motion.p>
          <motion.p variants={heroUp} className="text-white/80 text-base sm:text-lg max-w-md leading-relaxed">
            Custom apparel and printed products for businesses, schools, teams, events and more. Built with quality. Designed for you.
          </motion.p>
          <motion.div variants={heroUp} className="flex flex-wrap gap-4 mt-8">
            <Link to="/quote" data-testid="hero-quote-btn" className="group bg-[#f2f0ea] text-ink px-8 py-4 text-[13px] font-bold tracking-[0.18em] flex items-center gap-2 hover:bg-gold transition-colors">
              GET A QUOTE <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link to="/shop" data-testid="hero-shop-btn" className="border border-white/50 text-[#f2f0ea] px-8 py-4 text-[13px] font-bold tracking-[0.18em] hover:bg-white/10 transition-colors">
              SHOP NOW
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
    api.get("/gallery").then(({ data }) => setGallery(data)).catch(() => setGallery([]));
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />

      {/* Feature strip */}
      <section className="bg-ink text-[#f2f0ea]" data-testid="feature-strip">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-white/10">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="flex items-start gap-4 py-8 px-2 lg:px-8">
                <f.icon className="text-gold shrink-0 mt-1" size={30} strokeWidth={1.4} />
                <div>
                  <div className="font-display text-sm tracking-[0.14em]">{f.title}</div>
                  <p className="text-white/55 text-sm mt-1 leading-snug">{f.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Brands */}
      <section className="bg-paper py-12 border-b border-line" data-testid="brands">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
          <p className="text-center text-[11px] tracking-[0.3em] text-navy/50 font-semibold mb-7">PREMIUM BLANKS WE PRINT ON</p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 sm:gap-x-16 gap-y-5">
            {BRANDS.map((b) => (
              <span key={b} className="font-display text-2xl sm:text-3xl text-navy/70 hover:text-navy tracking-[0.03em] transition-colors">{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 bg-paper" data-testid="categories">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center mb-14">
            <p className="text-xs tracking-[0.3em] text-navy/60 font-semibold mb-3">WHAT WE CAN DO FOR YOU</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl">CUSTOM APPAREL FOR EVERY NEED</h2>
            <div className="w-16 h-1 bg-gold mx-auto mt-5" />
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {categories.map((c, i) => (
              <Reveal key={c.key} delay={(i % 3) * 0.1}>
                <Link to="/shop" data-testid={`category-${c.key}`}
                  onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`); e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`); }}
                  className="spotlight group relative block overflow-hidden bg-[#e9e6dd] aspect-square">
                  <img src={resolveImg(c.img)} alt={c.label} className="absolute inset-0 w-full h-full object-contain p-3 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-ink/90 via-ink/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
                    <div className="font-display text-lg sm:text-xl tracking-[0.08em] text-[#f2f0ea]">{c.label.toUpperCase()}</div>
                    <span className="inline-flex items-center gap-1 mt-2 text-[11px] tracking-[0.16em] text-gold font-semibold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400">
                      SHOP NOW <ArrowUpRight size={13} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Numbered manifesto chapters */}
      <section className="bg-navy-deep text-[#f2f0ea] py-24 overflow-hidden" style={{ background: "var(--navy-deep)" }} data-testid="manifesto">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
          <Reveal><p className="font-script text-gold text-5xl mb-2">Why Briars</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl mb-16 max-w-2xl">PRINTED WITH INTENTION. NOT JUST INK.</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-x-10 gap-y-12">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12}>
                <div className="border-t border-white/20 pt-6">
                  <div className="font-display text-6xl text-gold/90">{c.n}</div>
                  <h3 className="font-display text-xl tracking-[0.06em] mt-4 mb-3">{c.title.toUpperCase()}</h3>
                  <p className="text-white/60 leading-relaxed text-sm">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Easy as 1-2-3 + CTA */}
      <section className="py-24 bg-paper" data-testid="process">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center mb-14">
            <p className="text-xs tracking-[0.3em] text-navy/60 font-semibold mb-3">START YOUR PROJECT</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl">EASY AS 1-2-3</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-10 mb-16">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12} className="text-center">
                <div className="flex flex-col items-center">
                  <div className="relative mb-5">
                    <s.icon size={44} strokeWidth={1.3} className="text-navy" />
                    <span className="absolute -top-3 -right-4 font-display text-gold text-2xl">{s.n}</span>
                  </div>
                  <h3 className="font-display text-lg tracking-[0.06em] mb-2">{s.n}. {s.title}</h3>
                  <p className="text-navy/60 text-sm max-w-[240px] leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="bg-[#e9e6dd] p-8 sm:p-12 flex flex-col sm:flex-row items-center gap-6 justify-between">
              <div className="flex items-center gap-5">
                <FileText size={40} className="text-navy shrink-0" strokeWidth={1.3} />
                <div>
                  <h3 className="font-display text-2xl tracking-[0.04em]">READY TO GET STARTED?</h3>
                  <p className="text-navy/60 text-sm mt-1">Fill out our quick quote form and we'll respond with your custom quote.</p>
                </div>
              </div>
              <Link to="/quote" data-testid="cta-quote-btn" className="bg-ink text-[#f2f0ea] px-9 py-4 text-[13px] font-bold tracking-[0.18em] hover:bg-navy transition-colors whitespace-nowrap">
                GET A QUOTE
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Gallery marquee */}
      <section className="bg-ink py-6 marquee" data-testid="gallery-strip">
        <div className="marquee__track marquee__track--slow">
          {[...gallery, ...gallery, ...gallery].map((g, i) => (
            <Link to="/gallery" key={i} className="relative mx-2 w-[280px] h-[280px] shrink-0 overflow-hidden group inline-block bg-[#e9e6dd]">
              <img src={resolveImg(g.img)} alt={g.label} className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-5">
                <span className="font-display text-gold tracking-[0.1em]">{g.label.toUpperCase()}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
